import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { portalAdminApi, type ChangeAlert, type TeamRequest } from '../../api/portalAdmin'
import { ApiError } from '../../lib/api'
import { formatDateBR, formatDateTimeBR } from '../../lib/format'
import { Spinner } from '../Spinner'

// O que o cliente manda pelo portal e espera a equipe: pedidos e "Não reconheço" (alteração
// na conta que ele não fez). Usado na Inteligência (todos os clientes) e na aba Portal.

const errorText = (err: unknown) => (err instanceof ApiError || err instanceof Error ? err.message : 'Algo deu errado')

/** Invalida tudo que mostra pedidos e alertas (Inteligência, aba Portal, Dashboard). */
function useRefreshPortalFeedback() {
  const qc = useQueryClient()
  return () => {
    for (const key of ['portal-requests-team', 'portal-alerts', 'portal-admin', 'portal-admin-requests', 'portal-admin-changes', 'dashboard']) {
      qc.invalidateQueries({ queryKey: [key] })
    }
  }
}

const STATUS_LABEL: Record<TeamRequest['status'], { label: string; cls: string }> = {
  aberto: { label: 'Aberto', cls: 'bg-amber-100 text-amber-800' },
  respondido: { label: 'Respondido', cls: 'bg-brand-100 text-brand-700' },
  feito: { label: 'Feito', cls: 'bg-emerald-100 text-emerald-800' },
}

export function RequestList({ requests, showClient = true }: { requests: TeamRequest[]; showClient?: boolean }) {
  if (!requests.length) return <p className="text-sm text-slate-500">Nenhum pedido nos últimos 30 dias.</p>
  return (
    <ul className="space-y-3">
      {requests.map((r) => (
        <RequestItem key={r.id} r={r} showClient={showClient} />
      ))}
    </ul>
  )
}

function RequestItem({ r, showClient }: { r: TeamRequest; showClient: boolean }) {
  const refresh = useRefreshPortalFeedback()
  const [answer, setAnswer] = useState(r.answer ?? '')
  const [editing, setEditing] = useState(!r.answer && r.status === 'aberto')
  const save = useMutation({
    mutationFn: (body: { answer?: string; status?: TeamRequest['status'] }) => portalAdminApi.answerRequest(r.id, body),
    onSuccess: () => {
      setEditing(false)
      refresh()
    },
  })
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_LABEL[r.status].cls}`}>{STATUS_LABEL[r.status].label}</span>
        {showClient && (
          <Link to={`/clients/${r.client.id}?tab=portal`} className="font-semibold text-slate-900 hover:underline">
            {r.client.name}
          </Link>
        )}
        <span>
          {formatDateTimeBR(r.createdAt)}
          {r.userName ? ` · ${r.userName}` : ''}
        </span>
      </div>
      <p className="whitespace-pre-line text-sm text-slate-900">{r.text}</p>
      {r.answer && !editing && (
        <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <span className="text-xs text-slate-500">
            Resposta{r.answeredByName ? ` de ${r.answeredByName}` : ''}
            {r.answeredAt ? ` · ${formatDateBR(r.answeredAt)}` : ''} (o cliente vê no portal):
          </span>
          <p className="whitespace-pre-line">{r.answer}</p>
        </div>
      )}
      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Resposta para o cliente (aparece no portal)"
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={save.isPending || !answer.trim()}
              onClick={() => save.mutate({ answer: answer.trim(), status: 'respondido' })}
              className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Responder
            </button>
            <button
              type="button"
              disabled={save.isPending || !answer.trim()}
              onClick={() => save.mutate({ answer: answer.trim(), status: 'feito' })}
              className="rounded-md border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              Responder e marcar como feito
            </button>
            {r.answer && (
              <button type="button" onClick={() => setEditing(false)} className="text-xs text-slate-500 hover:underline">
                Cancelar
              </button>
            )}
            {save.isPending && <Spinner className="h-4 w-4" />}
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          <button type="button" onClick={() => setEditing(true)} className="font-medium text-brand-700 hover:underline">
            {r.answer ? 'editar resposta' : 'responder'}
          </button>
          {r.status !== 'feito' && (
            <button type="button" onClick={() => save.mutate({ status: 'feito' })} className="font-medium text-emerald-700 hover:underline">
              marcar como feito
            </button>
          )}
          {r.status !== 'aberto' && (
            <button type="button" onClick={() => save.mutate({ status: 'aberto' })} className="text-slate-500 hover:underline">
              reabrir
            </button>
          )}
        </div>
      )}
      {save.error && <p className="mt-1 text-xs text-rose-700">{errorText(save.error)}</p>}
    </li>
  )
}

export function ChangeAlerts({ alerts, showClient = true }: { alerts: ChangeAlert[]; showClient?: boolean }) {
  if (!alerts.length) return null
  return (
    <ul className="space-y-3">
      {alerts.map((a) => (
        <AlertItem key={`${a.clientId}-${a.day}`} a={a} showClient={showClient} />
      ))}
    </ul>
  )
}

function AlertItem({ a, showClient }: { a: ChangeAlert; showClient: boolean }) {
  const refresh = useRefreshPortalFeedback()
  const resolve = useMutation({ mutationFn: () => portalAdminApi.resolveAlert(a.clientId, a.day), onSuccess: refresh })
  return (
    <li className="rounded-lg border border-rose-200 bg-white p-3">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">Cliente não reconhece</span>
        {showClient && (
          <Link to={`/clients/${a.clientId}?tab=portal`} className="font-semibold text-slate-900 hover:underline">
            {a.clientName}
          </Link>
        )}
        <span>
          alterações de {formatDateBR(a.day)}
          {a.reviewedBy ? ` · respondido por ${a.reviewedBy}` : ''}
          {a.reviewedAt ? ` em ${formatDateTimeBR(a.reviewedAt)}` : ''}
        </span>
      </div>
      <ul className="space-y-0.5 text-sm text-slate-800">
        {a.changes.map((c) => (
          <li key={c.id}>
            • {c.text}
            {c.who ? <span className="text-xs text-slate-500"> — por {c.who}</span> : null}
          </li>
        ))}
      </ul>
      {a.note && <p className="mt-1 text-xs text-slate-600">Comentário do cliente: "{a.note}"</p>}
      <p className="mt-2 text-xs text-slate-500">
        Pode ser acesso indevido: confira quem tem acesso à conta (Ferramentas → Acesso e segurança) e fale com o cliente.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={resolve.isPending}
          onClick={() => resolve.mutate()}
          className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Marcar como resolvido
        </button>
        {resolve.isPending && <Spinner className="h-4 w-4" />}
        {resolve.error && <span className="text-xs text-rose-700">{errorText(resolve.error)}</span>}
      </div>
    </li>
  )
}
